import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Base from '../Base';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import Button from '../../components/elements/Button';
import { apiRequest } from '../../utils';

const Questions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [questionsPayload, setQuestionsPayload] = useState(null); // { survey_id, questions }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { [questionId]: value }

  // Load all questions once
  useEffect(() => {
    // fetchUserInfo will add auth headers + Selectedsurveyid like in /survey
    fetchUserInfo('/survey-questions', setQuestionsPayload, setLoading, setError, []);
  }, []);

  const questions = questionsPayload?.questions || [];
  const totalQuestions = questions.length;
  const currentQuestion = totalQuestions > 0 ? questions[currentIndex] : null;

  // Answer state helpers
  const getAnswerValue = (questionId) => {
    return answers[questionId] ?? '';
  };

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // Navigation
  const handlePrev = () => {
    setCurrentIndex((idx) => (idx > 0 ? idx - 1 : idx));
  };

  const handleNext = async () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex((idx) => idx + 1);
    } else {
      // Last question -> submit and show the score
      try {
        const payload = buildSubmitPayload();
        const result = await apiRequest('POST', '/survey-submit', payload);

        // If you later store answers in sessionStorage, clear them here:
        if (payload.survey_id) {
            sessionStorage.removeItem(`survey_answers_${payload.survey_id}`);
        }

        // Persist the result so a refresh on the results page keeps the score
        try {
            sessionStorage.setItem('lastQuizResult', JSON.stringify(result));
        } catch (e) { /* ignore storage errors */ }

        // Show the end-of-quiz score breakdown
        navigate('/survey-results', { state: result });
        } catch (err) {
        console.error('Failed to submit survey:', err);
        // you already have setError in state – reuse it:
        setError(err.message || 'Failed to submit survey');
        }
    }
  };

  const buildSubmitPayload = () => {
    const surveyId =
        questionsPayload?.survey_id ||
        sessionStorage.getItem('Selectedsurveyid') ||
        null;

    const formattedAnswers = questions.map((q) => {
        const rawValue = answers[q.id];
        const type = (q.question_type || '').toUpperCase();

        let answer_text = null;
        let answer_number = null;

        if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
        if (type === 'NUMERIC' || type === 'NUMERIC_RATING') {
            // numeric question
            answer_number = typeof rawValue === 'number'
            ? rawValue
            : Number(rawValue);
            if (Number.isNaN(answer_number)) {
            answer_number = null;
            }
        } else {
            // YesNo, Text, LongText (just store as text)
            answer_text = String(rawValue);
        }
        }

        return {
        question_id: q.id,
        answer_text,
        answer_number,
        };
    });

    // Optionally filter out completely empty answers
    const filteredAnswers = formattedAnswers.filter(
        (a) => a.answer_text !== null || a.answer_number !== null
    );

    return {
        survey_id: surveyId,
        answers: filteredAnswers,
    };
    };


  // Loading / error / empty states
  if (loading) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/survey" />
          <Title>
            <Icon name="clipboard-list" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('survey') || 'Survey'}
          </Title>
        </Card>
        <NotFound translationKey="loading" loading={true} />
      </Base>
    );
  }

  if (error) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/survey" />
          <Title>
            <Icon name="survey-xmark" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('survey') || 'Survey'}
          </Title>
        </Card>
        <NotFound translationKey="errorLoading" />
      </Base>
    );
  }

  if (!currentQuestion || totalQuestions === 0) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/survey" />
          <Title>
            <Icon name="clipboard-list" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('survey') || 'Survey'}
          </Title>
        </Card>
        <NotFound translationKey="noQuestions" />
      </Base>
    );
  }

  // ---- Input renderer ----

  const renderInputForQuestion = (question) => {
    const questionId = question.id;
    const value = getAnswerValue(questionId);
    const type = (question.question_type || '').toUpperCase();

    // NUMERIC_RATING, YESNO, TEXT, NUMERIC, LONGTEXT
    switch (type) {

      // 🔹 NEW: Multiple choice (attribute)
      case 'ATTRIBUTE': {
        const options = question.options || [];

        if (!options.length) {
          return (
            <div className="mb-3 text-muted">
              {t('noOptionsAvailable') || 'No options available.'}
            </div>
          );
        }

        return (
          <div className="mb-3">
            {options.map((opt, idx) => {
              const optValue = opt.value;
              const inputId = `q_${questionId}_opt_${idx}`;
              return (
                <div className="form-check mb-1" key={inputId}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={`q_${questionId}`}
                    id={inputId}
                    checked={value === optValue}
                    onChange={() => updateAnswer(questionId, optValue)}
                  />
                  <label className="form-check-label" htmlFor={inputId}>
                    {optValue}
                  </label>
                </div>
              );
            })}
          </div>
        );
      }


      case 'NUMERIC_RATING': {
        const max = question.maximum || 5;
        const min = 1;

        // default to middle or min if empty
        const numericValue =
          value === '' || value == null
            ? Math.round((min + max) / 2)
            : Number(value);

        return (
          <div className="mb-3">
            <label className="form-label d-flex justify-content-between">
              <span>
                {t('numericRatingLabel', { max }) ||
                  `Give a rating between ${min} and ${max}`}
              </span>
            </label>
            {/* Value above slider */}
            

            <input
              type="range"
              min={1}
              max={max}
              value={value || 0}
              className="form-range"
              onChange={(e) => updateAnswer(questionId, Number(e.target.value))}
            />
            <div className="d-flex justify-content-between small text-muted mt-1">
              <span>{min}</span>
              <div className="text-center mb-1">
                <b>({numericValue || 0})</b>
              </div>
              <span>{max}</span>
            </div>
          </div>
        );
      }


      case 'YESNO':
        return (
          <div className="mb-3">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={`q_${questionId}`}
                id={`q_${questionId}_yes`}
                checked={value === 'yes'}
                onChange={() => updateAnswer(questionId, 'yes')}
              />
              <label className="form-check-label" htmlFor={`q_${questionId}_yes`}>
                {t('yes') || 'Yes'}
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={`q_${questionId}`}
                id={`q_${questionId}_no`}
                checked={value === 'no'}
                onChange={() => updateAnswer(questionId, 'no')}
              />
              <label className="form-check-label" htmlFor={`q_${questionId}_no`}>
                {t('no') || 'No'}
              </label>
            </div>
          </div>
        );

      case 'TEXT':
        return (
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              value={value}
              onChange={(e) => updateAnswer(questionId, e.target.value)}
              placeholder={t('writeAnswer') || 'Write your answer...'}
            />
          </div>
        );

      case 'NUMERIC':
        return (
          <div className="mb-3">
            <input
              type="number"
              className="form-control"
              value={value}
              onChange={(e) => updateAnswer(questionId, e.target.value === '' ? '' : Number(e.target.value))}
              placeholder={t('writeNumberAnswer') || 'Write your answer...'}
            />
          </div>
        );

      case 'LONGTEXT':
        return (
          <div className="mb-3">
            <textarea
              className="form-control"
              rows={4}
              value={value}
              onChange={(e) => updateAnswer(questionId, e.target.value)}
              placeholder={t('writeAnswer') || 'Write your answer...'}
            />
          </div>
        );

      default:
        // Fallback: simple text input
        return (
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              value={value}
              onChange={(e) => updateAnswer(questionId, e.target.value)}
              placeholder={t('writeAnswer') || 'Write your answer...'}
            />
          </div>
        );
    }
  };

  const isLastQuestion = currentIndex === totalQuestions - 1;

  return (
    <Screen>
        <div className="top">
            <Card>
                <Back to="/survey" />
                

                {/* Header */}
                <div className="d-flex flex-column align-items-center mb-3 text-center">
                    <Title className="mb-2">
                    <Icon
                        name="survey-xmark"
                        className="me-2"
                        style={{ fontSize: '24px' }}
                        theme="primary"
                    />
                    {t('survey') || 'Survey'}
                    </Title>

                    <div style={{ fontSize: '13px' }} className="text-muted">
                    {t('questionProgress', {
                        current: currentIndex + 1,
                        total: totalQuestions,
                    }) ||
                        `Question ${currentIndex + 1} of ${totalQuestions}`}
                    </div>
                </div>

                <hr className='mb-4' />

               {/* Centered question + input */}
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  style={{ minHeight: '' }}
                >
                  <div>

                    {/* Question image (if any) */}
                    {currentQuestion.photo_url && (
                      <div className="mb-3 text-center">
                        <img
                          src={currentQuestion.photo_url}
                          alt="Question"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '220px',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    )}

                    {/* Question text */}
                    <h6 className="mb-4 text-center">
                      {currentQuestion.question_text}
                    </h6>

                    {/* Input below the question */}
                    {renderInputForQuestion(currentQuestion)}

                  </div>
                </div>


                {/* Navigation buttons */}
                <div className="d-flex justify-content-center mt-4 gap-2">
                    <Button
                        theme="secondary"
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        styles={{}}
                    >
                        {t('previous') || 'Previous'}
                    </Button>

                    <Button onClick={handleNext} styles={{}}>
                        {isLastQuestion
                        ? (t('submitSurvey') || 'Submit')
                        : (t('next') || 'Next')}
                    </Button>
                </div>
            </Card>

        </div>
    </Screen>
  );
};

export default Questions;
